import React, { useCallback, useMemo, useRef, useState } from 'react';

import {
	useClassNames,
} from '@common';

import './Popover.scss';

interface PopoverProps {
	children: JSX.Element | string | React.ReactNode;
	placement?: 'top' | 'bottom' | 'left' | 'right';
	content: JSX.Element | string | React.ReactNode;
	className?: string;
	disabled?: boolean;
}

function Popover(props: PopoverProps): JSX.Element {
	const {
		placement = 'bottom',
		className,
		children,
		content,
		disabled,
	} = props;

	const
		[showPopover, setShowPopover] = useState<boolean>(false),
		contentRef = useRef<HTMLDivElement>(null),
		targetRef = useRef<HTMLDivElement>(null);

	const onToggleShowPopover = useCallback(() => {
		if (disabled) {
			return;
		}

		setShowPopover(!showPopover);
		setTimeout(() => {
			const closePopover = (e) => {
				const target = e.target as HTMLElement;
				const isClickOver = !contentRef.current?.contains(target) && !targetRef.current?.contains(target);

				if (isClickOver) {
					setShowPopover(false);
					document.removeEventListener('click', closePopover);
				}
			};

			document.addEventListener('click', closePopover);
		});

	}, [setShowPopover, showPopover, disabled]);

	const popoverContent = useMemo(() => {
		return showPopover && (
			<div ref={contentRef} className="popover__content">
				{content}
			</div>
		);
	}, [content, showPopover]);

	const classes = useClassNames(
		'popover',
		placement,
		className,
		disabled && 'popover_disabled'
	);

	const target_classes = useClassNames(
		"popover__target",
		disabled && 'popover__target_disabled'
	);

	return (
		<div className={classes}>
			<div className={target_classes} ref={targetRef} onClick={onToggleShowPopover}>
				{children}
			</div>
			{popoverContent}
		</div>
	);
}

const MemoisedPopover = React.memo(Popover);

export {MemoisedPopover as Popover};